/**
 * CalendarDayCell Component
 *
 * Custom day cell renderer for react-native-calendars.
 * Displays date number and event text bars (max 3 visible).
 *
 * Follows single responsibility principle: only renders a single day cell.
 */

import { BorderRadius } from '@/constants/spacing';
import { Colors } from '@/constants/theme';
import { getBodyFont } from '@/constants/typography';
import { DynamicEventCalculator } from '@/lib/calendar/DynamicEventCalculator';
import { MonthlyCalendarManager } from '@/lib/calendar/MonthlyCalendarManager';
import { getLightBackground, getDarkBackground } from '@/lib/calendar/EventDisplayUtils';
import { DAY_HEIGHT, DAY_WIDTH } from '@/lib/calendar/MonthlyCalendarTheme';
import type { DateMarking, RNCalendarsEvent } from '@/types/monthly-calendar';
import { getContrastTextColor } from '@/lib/utils/color-contrast';
import { TaskCountResolver } from '@/lib/utils/TaskCountResolver';
import { TaskIndicator } from '@/components/shared/TaskIndicator';
import { memo, useCallback, useMemo } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import type { DateData } from 'react-native-calendars';

type CalendarDayCellProps = {
  date?: DateData;
  state?: 'disabled' | 'today' | 'selected' | '';
  marking?: DateMarking;
  onPress?: (day: DateData) => void;
  accentColor: string;
  colorScheme?: 'light' | 'dark';
  customStyles: {
    dayCell: object;
    dayNumber: object;
    selectedCell: object;
    eventBar: object;
    eventBarText: object;
    allDayEventBar: object;
    overflowIndicator: object;
  };
};

/**
 * EventBar Component
 *
 * Renders a single event bar with title, color, and family member dot.
 * All-day events have filled background, timed events have transparent background with dot.
 */
const EventBar = memo(function EventBar({
  event,
  customStyles,
  accentColor,
  totalEventCount,
  isDarkMode,
}: {
  event: RNCalendarsEvent;
  customStyles: CalendarDayCellProps['customStyles'];
  accentColor: string;
  totalEventCount: number;
  isDarkMode?: boolean;
}) {
  const allowTwoLines = totalEventCount < 3;
  const displayTitle = allowTwoLines
    ? event.title
    : MonthlyCalendarManager.truncateEventTitle(event.title, 12);
  const dotColor = event.familyMemberColor || accentColor;
  const fadedColor = isDarkMode ? getDarkBackground(dotColor) : getLightBackground(dotColor);

  // Different styling for all-day vs timed events
  // All-day: faded background, timed: left colored strip + faded background
  const eventBarStyle = event.isAllDay
    ? { backgroundColor: fadedColor }
    : { backgroundColor: 'transparent', borderWidth: 0, borderLeftWidth: 2.5, borderLeftColor: dotColor };

  // Adjust height and alignment for 2-line text when allowed
  const heightStyle = allowTwoLines
    ? { height: 'auto' as const, minHeight: 12, alignItems: 'flex-start' as const }
    : {};

  const contrastColor = getContrastTextColor(fadedColor);

  const textStyle = event.isAllDay
    ? [customStyles.eventBarText, { color: contrastColor }]
    : [customStyles.eventBarText, { color: contrastColor, flexShrink: 1 }];

  const taskCount = TaskCountResolver.fromCounts(event.openTaskCount, event.totalTaskCount);

  return (
    <View
      style={[customStyles.eventBar, eventBarStyle, heightStyle, { overflow: 'hidden', flexDirection: 'row', alignItems: 'center' }]}
      pointerEvents="none"
    >
      <Text style={[...textStyle, { flex: 1 }]} numberOfLines={allowTwoLines ? 2 : 1}>
        {displayTitle}
      </Text>
      {taskCount && (
        <View style={{ marginLeft: 2 }}>
          <TaskIndicator taskCount={taskCount} size="small" />
        </View>
      )}
    </View>
  );
});

/**
 * CalendarDayCell Component
 *
 * Renders a day cell with date number and event bars.
 */
export const CalendarDayCell = memo(function CalendarDayCell({
  date,
  state,
  marking,
  onPress,
  accentColor,
  colorScheme,
  customStyles,
}: CalendarDayCellProps) {
  const isDarkMode = colorScheme === 'dark';
  const palette = Colors[colorScheme ?? 'light'];

  const events = marking?.events || [];
  const isSelected = marking?.selected || false;
  const isToday = state === 'today' || marking?.today;
  const isDisabled = state === 'disabled';

  const handlePress = useCallback(() => {
    if (!isDisabled && date) {
      onPress?.(date);
    }
  }, [date, onPress, isDisabled]);

  // Dynamically calculate how many events fit based on cell height
  const maxSlots = useMemo(() => DynamicEventCalculator.calculateMaxSlots(DAY_HEIGHT), []);
  const { eventsToShow, overflowCount } = useMemo(
    () => DynamicEventCalculator.calculateEventDisplay(events.length, maxSlots),
    [events.length, maxSlots]
  );

  // Handle empty cell case (after hooks to satisfy rules-of-hooks)
  if (!date) {
    return <View style={styles.emptyCell} />;
  }

  const { day } = date;
  const visibleEvents = events.slice(0, eventsToShow);

  // Build cell container style with theme-aware background
  const cellStyle: any[] = [
    styles.dayCell,
    { backgroundColor: palette.cardBackground, borderColor: palette.border },
    isToday && customStyles.selectedCell,
    isSelected && !isToday && styles.selectedBorder,
    isSelected && !isToday && { borderColor: accentColor },
    isDisabled && [styles.outsideMonthCell, { backgroundColor: palette.backgroundLight }],
  ];

  // Build text style
  // Today uses palette.primary (blue/orange) for mode-appropriate accent
  // Selected (non-today) uses accentColor (user's preferred color)
  const textStyle: any[] = [
    styles.dayNumber,
    { color: palette.text },
    isToday && {
      color: palette.primary,
      fontWeight: '600' as const,
    },
    isSelected && !isToday && {
      color: accentColor,
      fontWeight: '600' as const,
    },
    isDisabled && { color: palette.textMuted },
  ];

  return (
    <TouchableOpacity
      style={cellStyle}
      onPress={handlePress}
      activeOpacity={0.7}
      disabled={isDisabled}
    >
      <Text style={textStyle}>{day}</Text>

      <View style={styles.eventsContainer}>
        {visibleEvents.map((event, index) => (
          <EventBar
            key={`${event.id}-${index}`}
            event={event}
            customStyles={customStyles}
            accentColor={accentColor}
            totalEventCount={events.length}
            isDarkMode={isDarkMode}
          />
        ))}

        {overflowCount > 0 && (
          <Text style={customStyles.overflowIndicator}>
            +{overflowCount} more
          </Text>
        )}
      </View>
    </TouchableOpacity>
  );
});

const styles = StyleSheet.create({
  emptyCell: {
    width: DAY_WIDTH,
    height: DAY_HEIGHT,
  },
  dayCell: {
    width: DAY_WIDTH,
    height: DAY_HEIGHT,
    padding: 2,
    // backgroundColor and borderColor applied dynamically via palette
    borderWidth: 0.5,
    borderRadius: BorderRadius.xs,
  },
  selectedBorder: {
    borderWidth: 0.8,
    borderRadius: BorderRadius.xs,
  },
  dayNumber: {
    fontSize: 12,
    fontWeight: '500',
    fontFamily: getBodyFont(),
    paddingTop: 1,
    textAlign: 'center',
    // color applied dynamically via palette
  },
  eventsContainer: {
    flex: 1,
    paddingTop: 1,
    gap: 1,
    overflow: 'hidden',
  },
  outsideMonthCell: {
    opacity: 0.4,
    // backgroundColor applied dynamically via palette
  },
  disabledText: {
    // color applied dynamically via palette
  },
});
